import React from 'react';
import { formatDateLocale } from '../utils/dateFormat';
import { useTranslation } from 'react-i18next';
import { CancellationPolicyRef } from './legal/CancellationPolicyRef';
import { CheckCircle } from 'lucide-react';
import { currencySymbol } from '../utils/currencyPolicy';
import ProviderTaxBreakdown from './ProviderTaxBreakdown';
import { PaymentPlanSchedule } from './payments/PaymentPlanSchedule';

// OFFER-LIFECYCLE-PRODUCTION-TRUTH-CLOSURE — ONE read-only projection of an
// immutable quote/version's commercial facts, consumed by the Sales Rep offer
// view and the Coordinator/Clinic Admin offer detail. Reads canonical
// snake_case quote fields + backend `financial_summary` only; never recomputes.
const money = (ccy, v) => `${currencySymbol(ccy)}${Number(v ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Section = ({ title, children, testId, className = '' }) => (
  <div className={`rounded-lg p-4 ${className}`} data-testid={testId}>
    <h3 className="font-semibold text-gray-900 mb-2 text-sm">{title}</h3>
    {children}
  </div>
);

export const OfferCommercialFacts = ({ offer, requestedTreatment, language }) => {
  const { t } = useTranslation();
  if (!offer) return null;
  const fs = offer.financial_summary || {};
  const ccy = offer.currency;
  const additional = offer.additional_services || offer.additionalServices || [];
  const included = offer.included_items || offer.includedItems || [];
  const validUntil = offer.valid_until || offer.validUntil;
  const snap = offer.cancellation_policy_snapshot;
  const locale = String(language || 'en').slice(0, 2).toLowerCase();
  const policyBody = snap?.body ? (snap.body[locale] || snap.body.en || Object.values(snap.body).find(v => typeof v === 'string' && v.trim())) : null;
  const hotelNights = offer.hotel_nights ?? offer.hotelNights;
  const hotelType = offer.hotel_type ?? offer.hotelType;
  const transfer = offer.airport_transfer ?? offer.airportTransfer;
  const dates = offer.proposed_dates || offer.proposedDates;
  const notes = offer.notes_patient_visible || offer.notes;
  const requested = requestedTreatment || offer.requested_treatment_display_name || offer.requested_treatment;

  return (
    <div className="space-y-4" data-testid="offer-commercial-facts">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Section title={t('provider.salesRep.offeredTreatment', 'Offered Treatment')} className="bg-gray-50" testId="ocf-offered-treatment">
          <p className="text-base font-medium text-blue-700" data-testid="sent-offer-treatment">{offer.treatment_name || offer.treatment || '—'}</p>
        </Section>
        {requested && (
          <Section title={t('offer.requestedTreatment', 'Requested Treatment')} className="bg-gray-50" testId="ocf-requested-treatment">
            <p className="text-base font-medium text-gray-800">{requested}</p>
          </Section>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="bg-blue-50 rounded-lg p-4">
          <p className="text-xs text-gray-600">{t('provider.salesRep.totalPrice', 'Total Price')}</p>
          <p className="text-2xl font-bold text-blue-700" data-testid="ocf-total">{money(ccy, fs.total)}</p>
          <p className="text-xs text-gray-500 mt-1" data-testid="ocf-breakdown">
            {t('offer.pricing.base', 'Base')}: {money(ccy, fs.base_price ?? offer.base_price)}
            {(fs.additional_services_total ?? 0) > 0 && ` + ${money(ccy, fs.additional_services_total)}`}
          </p>
          <div className="mt-2">
            <ProviderTaxBreakdown
              financialSummary={fs}
              currencySym={currencySymbol(ccy)}
              testId="ocf-provider-tax"
            />
          </div>
        </div>
        <div className="bg-green-50 rounded-lg p-4">
          <p className="text-xs text-gray-600">{t('provider.salesRep.depositRequired', 'Deposit')}</p>
          <p className="text-2xl font-bold text-green-700" data-testid="ocf-deposit">{money(ccy, fs.deposit ?? fs.deposit_amount)}</p>
          <p className="text-xs text-gray-500 mt-1" data-testid="ocf-deposit-rule">
            {offer.deposit_type === 'percentage'
              ? `${Number(offer.deposit_value ?? 0)}% ${t('offer.ofTotal', 'of total')}`
              : t('offer.fixedDeposit', 'Fixed deposit')}
            {' · '}{t('offer.balance', 'Balance')}: {money(ccy, fs.balance)}
          </p>
        </div>
      </div>

      <Section title={t('provider.salesRep.paymentStructure', 'Payment Structure')} className="bg-amber-50" testId="ocf-payment">
        {/* TREATX-PLAN-1 — print the real collections. The previous summary
            claimed "N EQUAL installments of X", which was simply untrue for any
            balance that does not divide exactly. */}
        <PaymentPlanSchedule
          summary={fs}
          currency={ccy}
          depositType={offer.deposit_type}
          depositValue={offer.deposit_value}
          testId="ocf-payment-plan"
        />
      </Section>

      {additional.length > 0 && (
        <Section title={t('offer.additionalServices', 'Additional Services')} className="bg-white border" testId="ocf-additional-services">
          <table className="w-full text-sm">
            <thead className="text-xs text-gray-500">
              <tr><th className="text-left font-medium">{t('offer.service', 'Service')}</th><th className="text-right font-medium">{t('offer.qty', 'Qty')}</th><th className="text-right font-medium">{t('offer.unitPrice', 'Unit')}</th><th className="text-right font-medium">{t('offer.lineTotal', 'Total')}</th></tr>
            </thead>
            <tbody>
              {additional.map((s, i) => {
                const qty = Number(s.quantity || 1);
                return (
                  <tr key={i} className="border-t" data-testid={`ocf-service-${i}`}>
                    <td className="py-1 text-gray-700">{s.name}</td>
                    <td className="py-1 text-right">{qty}</td>
                    <td className="py-1 text-right">{money(ccy, s.price)}</td>
                    <td className="py-1 text-right font-medium">{money(ccy, Number(s.price || 0) * qty)}</td>
                  </tr>
                );
              })}
              <tr className="border-t font-medium"><td colSpan={3} className="py-1 text-right text-gray-600">{t('offer.additionalTotal', 'Additional services total')}</td><td className="py-1 text-right">{money(ccy, fs.additional_services_total)}</td></tr>
            </tbody>
          </table>
        </Section>
      )}

      {included.length > 0 && (
        <Section title={t('provider.salesRep.includedInPackage', 'Included in Package')} testId="ocf-included">
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1">
            {included.map((item, idx) => (
              <li key={idx} className="flex items-center gap-2 text-sm text-gray-700">
                <CheckCircle className="h-4 w-4 text-green-500 shrink-0" />{typeof item === 'string' ? item : item.name}
              </li>
            ))}
          </ul>
        </Section>
      )}

      {(hotelNights || hotelType || transfer != null || dates) && (
        <Section title={t('provider.salesRep.travelAccommodation', 'Travel & Stay')} className="bg-gray-50" testId="ocf-travel">
          <div className="grid grid-cols-2 gap-3 text-sm">
            {hotelNights ? <div><p className="text-gray-500">{t('offer.hotelNights', 'Hotel Nights')}</p><p className="font-medium">{hotelNights}</p></div> : null}
            {hotelType ? <div><p className="text-gray-500">{t('offer.hotelType', 'Hotel Type')}</p><p className="font-medium">{hotelType}</p></div> : null}
            {transfer != null && <div><p className="text-gray-500">{t('offer.airportTransfer', 'Airport Transfer')}</p><p className="font-medium">{transfer ? t('offer.includedFlag', 'Included') : t('offer.notIncluded', 'Not included')}</p></div>}
            {dates && <div><p className="text-gray-500">{t('provider.salesRep.proposedDates', 'Proposed Dates')}</p><p className="font-medium whitespace-pre-line">{dates}</p></div>}
          </div>
        </Section>
      )}

      {notes && (
        <Section title={t('provider.salesRep.notesToPatient', 'Additional Information')} testId="ocf-notes">
          <p className="text-sm text-gray-700 whitespace-pre-line">{notes}</p>
        </Section>
      )}

      {offer.terms && (
        <Section title={t('provider.salesRep.paymentTerms', 'Terms & Conditions')} className="bg-gray-50" testId="ocf-terms">
          <p className="text-sm text-gray-700 whitespace-pre-line">{offer.terms}</p>
        </Section>
      )}

      <Section title={t('patient.offers.cancellationPolicy', 'Cancellation & Refund Policy')} className="bg-gray-50" testId="ocf-cancellation">
        <CancellationPolicyRef
          snapshot={snap}
          language={language}
          testId="ocf-cancellation-ref"
        />
      </Section>

      <div className="flex items-center justify-between text-xs text-gray-500 pt-2 border-t" data-testid="ocf-validity">
        <span>{t('patient.quote.validity', 'Validity')}: {validUntil ? formatDateLocale(validUntil) : (offer.valid_days ? `${offer.valid_days} ${t('offer.days', 'days')}` : '—')}</span>
        <span>v{offer.version_number || offer.version || 1}{offer.is_latest ? ` · ${t('offer.latest', 'latest')}` : ''}</span>
      </div>
    </div>
  );
};

export default OfferCommercialFacts;
